
import React, { useState, useEffect } from 'react';
import { UserRole, AppState, BetOutcome, Bet, User, League, Player, Market } from './types.ts';
import { supabase } from './lib/supabase.ts';
import Sidebar from './components/Sidebar.tsx';
import Navbar from './components/Navbar.tsx';
import Dashboard from './components/Dashboard.tsx';
import BetHistory from './components/BetHistory.tsx';
import AdminPanel from './components/AdminPanel.tsx';
import StatsView from './components/StatsView.tsx';
import ProfileView from './components/ProfileView.tsx';
import LoginView from './components/LoginView.tsx';
import LandingPage from './components/LandingPage.tsx';
import Footer from './components/Footer.tsx';
import SalesNotification from './components/SalesNotification.tsx';
import FinancialView from './components/FinancialView.tsx';
import SubscriptionView from './components/SubscriptionView.tsx';
import WhatsAppButton from './components/WhatsAppButton.tsx';

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAuth, setShowAuth] = useState(false);
  const [state, setState] = useState<AppState>({
    currentUser: { id: '', name: 'Visitante', role: UserRole.USER, active: true },
    users: [],
    leagues: [],
    players: [],
    markets: [],
    bets: [],
  });

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        if (error) throw error;

        setSession(currentSession);
        if (currentSession) {
          await fetchInitialData(currentSession.user.id, currentSession.user.email);
        } else {
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error('Erro ao inicializar Supabase:', error);
        setInitError(error.message);
        setIsLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession) {
        fetchInitialData(newSession.user.id, newSession.user.email);
      } else {
        setIsLoading(false);
        setState(prev => ({ ...prev, currentUser: { id: '', name: 'Visitante', role: UserRole.USER, active: true } }));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchInitialData = async (userId: string, userEmail?: string) => {
    setIsLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();

      if (profile && !profile.active) {
        alert("Sua conta está desativada.");
        await supabase.auth.signOut();
        return;
      }

      const [leaguesRes, playersRes, marketsRes, betsRes, usersRes] = await Promise.all([
        supabase.from('leagues').select('*').order('name'),
        supabase.from('players').select('*').order('name'),
        supabase.from('markets').select('*').order('name'),
        supabase.from('bets').select('*').eq('user_id', userId).order('date', { ascending: false }),
        supabase.from('profiles').select('*').order('created_at', { ascending: false })
      ]);

      setState({
        currentUser: profile || {
          id: userId,
          name: userEmail?.split('@')[0] || 'Novo Trader',
          role: UserRole.USER,
          active: true,
          email: userEmail
        },
        users: usersRes.data || [],
        leagues: leaguesRes.data || [],
        players: playersRes.data || [],
        markets: marketsRes.data || [],
        bets: betsRes.data || [],
      });
    } catch (error) {
      console.error('Erro na sincronização de dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setShowAuth(false);
    setActiveTab('dashboard');
  };

  const calculateProfit = (stake: number, odd: number, outcome: BetOutcome) => {
    if (outcome === BetOutcome.GREEN) return stake * (odd - 1);
    if (outcome === BetOutcome.RED) return -stake;
    return 0;
  };

  const handleAddBet = async (newBet: Omit<Bet, 'id' | 'profit'>) => {
    const profit = calculateProfit(newBet.stake, newBet.odd, newBet.outcome);
    const payload = { ...newBet, profit };

    const { data, error } = await supabase.from('bets').insert([payload]).select().maybeSingle();

    if (error) {
      console.error("Erro Supabase:", error);
      return alert("Erro ao salvar: " + error.message);
    }

    if (data) {
      setState(prev => ({ ...prev, bets: [data, ...prev.bets] }));
    }
  };

  const handleEditBet = async (betId: string, updatedBet: Partial<Bet>) => {
    const currentBet = state.bets.find(b => b.id === betId);
    if (!currentBet) return;

    const merged = { ...currentBet, ...updatedBet };
    const profit = calculateProfit(merged.stake, merged.odd, merged.outcome);

    const { data, error } = await supabase.from('bets').update({ ...updatedBet, profit }).eq('id', betId).select().maybeSingle();

    if (error) {
      alert("Erro ao editar: " + error.message);
      return;
    }

    if (data) {
      setState(prev => ({
        ...prev,
        bets: prev.bets.map(b => b.id === betId ? data : b)
      }));
    }
  };

  const handleUpdateOutcome = async (betId: string, outcome: BetOutcome) => {
    const bet = state.bets.find(b => b.id === betId);
    if (!bet) return;
    const profit = calculateProfit(bet.stake, bet.odd, outcome);

    const { data } = await supabase.from('bets').update({ outcome, profit }).eq('id', betId).select().maybeSingle();
    if (data) setState(prev => ({ ...prev, bets: prev.bets.map(b => b.id === betId ? data : b) }));
  };

  const handleUpdateUser = async (updatedData: Partial<User>) => {
    const { data } = await supabase.from('profiles').update(updatedData).eq('id', state.currentUser.id).select().maybeSingle();
    if (data) setState(prev => ({ ...prev, currentUser: data, users: prev.users.map(u => u.id === data.id ? data : u) }));
  };

  const handleAddLeague = async (name: string): Promise<boolean> => {
    const normalizedName = name.trim();
    if (!normalizedName) return false;

    const exists = state.leagues.some(l => l.name.toLowerCase() === normalizedName.toLowerCase());
    if (exists) {
      alert("Esta liga já está cadastrada.");
      return false;
    }

    const { data, error } = await supabase.from('leagues').insert([{ name: normalizedName }]).select().maybeSingle();
    if (error) {
      alert("Erro: " + error.message);
      return false;
    } else if (data) {
      setState(prev => ({ ...prev, leagues: [...prev.leagues, data].sort((a, b) => a.name.localeCompare(b.name)) }));
      return true;
    }
    return false;
  };

  const handleAddPlayer = async (name: string, league_id: string): Promise<boolean> => {
    const normalizedName = name.trim();
    if (!normalizedName || !league_id) return false;

    const exists = state.players.some(p =>
      p.name.toLowerCase() === normalizedName.toLowerCase() && p.league_id === league_id
    );
    if (exists) {
      alert("Este jogador já está cadastrado nesta liga.");
      return false;
    }

    const { data, error } = await supabase.from('players').insert([{ name: normalizedName, league_id }]).select().maybeSingle();
    if (error) {
      alert("Erro: " + error.message);
      return false;
    } else if (data) {
      setState(prev => ({ ...prev, players: [...prev.players, data].sort((a, b) => a.name.localeCompare(b.name)) }));
      return true;
    }
    return false;
  };

  const handleAddMarket = async (name: string): Promise<boolean> => {
    const normalizedName = name.trim();
    if (!normalizedName) return false;

    const exists = state.markets.some(m => m.name.toLowerCase() === normalizedName.toLowerCase());
    if (exists) {
      alert("Este mercado já está cadastrado.");
      return false;
    }

    const { data, error } = await supabase.from('markets').insert([{ name: normalizedName }]).select().maybeSingle();
    if (error) {
      alert("Erro: " + error.message);
      return false;
    } else if (data) {
      setState(prev => ({ ...prev, markets: [...prev.markets, data].sort((a, b) => a.name.localeCompare(b.name)) }));
      return true;
    }
    return false;
  };

  const handleUpdateLeague = async (id: string, name: string) => {
    const { data, error } = await supabase.from('leagues').update({ name }).eq('id', id).select().maybeSingle();
    if (error) alert("Erro ao atualizar liga: " + error.message);
    else if (data) setState(prev => ({ ...prev, leagues: prev.leagues.map(l => l.id === id ? data : l).sort((a, b) => a.name.localeCompare(b.name)) }));
  };

  const handleUpdatePlayer = async (id: string, name: string, league_id: string) => {
    const { data, error } = await supabase.from('players').update({ name, league_id }).eq('id', id).select().maybeSingle();
    if (error) alert("Erro ao atualizar jogador: " + error.message);
    else if (data) setState(prev => ({ ...prev, players: prev.players.map(p => p.id === id ? data : p).sort((a, b) => a.name.localeCompare(b.name)) }));
  };

  const handleUpdateMarket = async (id: string, name: string) => {
    const { data, error } = await supabase.from('markets').update({ name }).eq('id', id).select().maybeSingle();
    if (error) alert("Erro ao atualizar mercado: " + error.message);
    else if (data) setState(prev => ({ ...prev, markets: prev.markets.map(m => m.id === id ? data : m).sort((a, b) => a.name.localeCompare(b.name)) }));
  };

  const handleToggleUserStatus = async (userId: string) => {
    const user = state.users.find(u => u.id === userId);
    if (!user) return;
    const { data, error } = await supabase.from('profiles').update({ active: !user.active }).eq('id', userId).select();
    if (error) alert("Erro: " + error.message);
    else if (data && data.length > 0) setState(prev => ({ ...prev, users: prev.users.map(u => u.id === userId ? data[0] : u) }));
  };

  const handleUpdateUserRole = async (userId: string, role: UserRole) => {
    const { data, error } = await supabase.from('profiles').update({ role }).eq('id', userId).select();
    if (error) alert("Erro ao mudar cargo: " + error.message);
    else if (data && data.length > 0) {
      setState(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === userId ? data[0] : u),
        currentUser: userId === state.currentUser.id ? data[0] : prev.currentUser
      }));
    }
  };

  const renderContent = () => {
    if (isLoading) return <div className="p-10 text-center uppercase tracking-widest text-xs opacity-50 text-[#00ff66]">Carregando Banco de Dados...</div>;
    if (initError) return <div className="p-10 text-center text-red-500 uppercase font-bold">{initError}</div>;

    switch (activeTab) {
      case 'dashboard': return <Dashboard state={state} onAddBet={handleAddBet} />;
      case 'history': return <BetHistory state={state} onUpdateOutcome={handleUpdateOutcome} onEditBet={handleEditBet} onAddBet={handleAddBet} />;
      case 'stats': return <StatsView state={state} onAddBet={handleAddBet} />;
      case 'profile': return <ProfileView user={state.currentUser} onUpdate={handleUpdateUser} />;
      case 'finance': return <FinancialView state={state} />;
      case 'admin': return <AdminPanel
        state={state}
        onAddLeague={handleAddLeague}
        onAddPlayer={handleAddPlayer}
        onAddMarket={handleAddMarket}
        onUpdateLeague={handleUpdateLeague}
        onUpdatePlayer={handleUpdatePlayer}
        onUpdateMarket={handleUpdateMarket}
        onToggleUserStatus={handleToggleUserStatus}
        onUpdateUserRole={handleUpdateUserRole}
      />;
      default: return <Dashboard state={state} onAddBet={handleAddBet} />;
    }
  };

  if (!session && !isLoading) {
    if (showAuth) return <LoginView onLogin={() => { }} onBack={() => setShowAuth(false)} />;
    return (
      <>
        <LandingPage onEnter={() => setShowAuth(true)} />
        <SalesNotification />
        <WhatsAppButton />
      </>
    );
  }

  const isSubscriptionActive = (expiry?: string | null) => {
    if (!expiry) return false;
    return new Date(expiry) > new Date();
  };

  const needsSubscription = state.currentUser.role === UserRole.USER && !isSubscriptionActive(state.currentUser.subscription_expires_at);

  if (needsSubscription && activeTab !== 'profile') {
    return (
      <>
        <SubscriptionView user={state.currentUser} onSignOut={handleLogout} />
        <WhatsAppButton />
      </>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden text-[#eaecef]">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} userRole={state.currentUser.role} onLogout={handleLogout} />
      <div className="flex-1 flex flex-col min-w-0 bg-[#0b0e11]">
        <Navbar user={state.currentUser} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {renderContent()}
            <Footer />
          </div>
        </main>
      </div>
      <WhatsAppButton />
    </div>
  );
};

export default App;
